import { createContext, useCallback, useContext, useMemo } from 'react';
import { flushSync } from 'react-dom';

import { useUnloadPromptContext } from '@/navigation/unloadPrompt.context';

import {
  OpenTrowser,
  TrowserData,
  TrowserKey,
  TrowserSection,
} from './trowser.types';

export interface OpenTrowserOptions<K extends TrowserKey = TrowserKey> {
  /** The instance being edited (e.g. an entity id), if not implied by the route. */
  target?: string;
  section?: TrowserSection<K>;
  /** Transient, non-URL data carried with this open. */
  data?: TrowserData<K>;
}

/** Transient per-trowser data, typed by key. */
export type TrowserDataByKey = {
  [K in TrowserKey]?: TrowserData<K>;
};

export interface TrowserContextValue {
  /** Open trowsers, bottom→top (topmost last). */
  openTrowsers: OpenTrowser[];
  section: string | null;
  /** Transient data per trowser; absent on reload (re-supplied via setModalData). */
  modalDataByKey: TrowserDataByKey;
  openTrowser: <K extends TrowserKey>(
    key: K,
    options?: OpenTrowserOptions<K>
  ) => void;
  /** Close a trowser (defaults to the topmost). */
  closeTrowser: (key?: TrowserKey) => void;
  setSection: (section: string | null) => void;
  setModalData: <K extends TrowserKey>(key: K, data: TrowserData<K>) => void;
}

// No-op default so surfaces can render outside the provider (tests/stories).
const NOOP_CONTEXT: TrowserContextValue = {
  openTrowsers: [],
  section: null,
  modalDataByKey: {},
  openTrowser: () => undefined,
  closeTrowser: () => undefined,
  setSection: () => undefined,
  setModalData: () => undefined,
};

export const TrowserContext = createContext<TrowserContextValue>(NOOP_CONTEXT);

export function useTrowserContext(): TrowserContextValue {
  return useContext(TrowserContext);
}

export interface TrowserState<K extends TrowserKey = TrowserKey> {
  isOpen: boolean;
  target: string | undefined;
  /** Only set when this trowser is topmost. */
  section: string | null;
  /** Transient open data; undefined on reload. */
  data: TrowserData<K> | undefined;
  open: (options?: OpenTrowserOptions<K>) => void;
  close: () => void;
  setSection: (section: TrowserSection<K>) => void;
  setData: (data: TrowserData<K>) => void;
}

/** Read/drive a single trowser's URL-backed open state (see `TrowserProvider`). */
export function useTrowser<K extends TrowserKey>(key: K): TrowserState<K> {
  const {
    openTrowsers,
    section,
    modalDataByKey,
    openTrowser,
    closeTrowser,
    setSection,
    setModalData,
  } = useTrowserContext();
  const { setShouldBlockNavCtx } = useUnloadPromptContext();

  const index = openTrowsers.findIndex((trowser) => trowser.key === key);
  const isOpen = index !== -1;
  const isTopmost = isOpen && index === openTrowsers.length - 1;
  const target = isOpen ? openTrowsers[index]?.target : undefined;
  const data = modalDataByKey[key];

  const open = useCallback(
    (options?: OpenTrowserOptions<K>) => openTrowser(key, options),
    [openTrowser, key]
  );
  const close = useCallback(() => {
    // Programmatic close: the trowser's own flow already decided to close, so
    // clear the unsaved-changes blocker before closeTrowser issues its history
    // POP — otherwise the blocker would re-prompt (or, after a save whose dirty
    // subscriber hasn't flushed yet, prompt spuriously). flushSync so the
    // blocker observes the cleared flag before we navigate. Browser Back still
    // prompts: it doesn't route through here.
    flushSync(() => setShouldBlockNavCtx(false));
    closeTrowser(key);
  }, [closeTrowser, key, setShouldBlockNavCtx]);
  const setData = useCallback(
    (nextData: TrowserData<K>) => setModalData(key, nextData),
    [setModalData, key]
  );
  const setTypedSection = useCallback(
    (nextSection: TrowserSection<K>) => setSection(nextSection),
    [setSection]
  );

  return useMemo(
    () => ({
      isOpen,
      target,
      section: isTopmost ? section : null,
      data,
      open,
      close,
      setSection: setTypedSection,
      setData,
    }),
    [
      isOpen,
      target,
      isTopmost,
      section,
      data,
      open,
      close,
      setTypedSection,
      setData,
    ]
  );
}
